import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

// Create axios instance outside the component to prevent re-creation on every render
const wishlistApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
  withCredentials: true,
});

// Intercepteur pour ajouter le token d'authentification
wishlistApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const WishlistContext = createContext();

const initialState = {
  items: [],
  loading: false,
  error: null,
  initialized: false
};

const wishlistReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    
    case 'SET_WISHLIST':
      return { 
        ...state, 
        items: action.payload, 
        loading: false, 
        error: null,
        initialized: true 
      };
    
    case 'ADD_ITEM':
      return { 
        ...state, 
        items: [...state.items, action.payload],
        error: null 
      };
    
    case 'REMOVE_ITEM':
      return { 
        ...state, 
        items: state.items.filter(item => item.id !== action.payload),
        error: null 
      };
    
    case 'CLEAR_WISHLIST':
      return { ...state, items: [], error: null };
    
    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state.items.map(item => 
          item.id === action.payload.id ? { ...item, ...action.payload } : item
        )
      };
    
    default:
      return state;
  }
};

export const WishlistProvider = ({ children }) => {
  const [state, dispatch] = useReducer(wishlistReducer, initialState);
  const { isAuthenticated, user } = useAuth();

  // Charger la wishlist depuis le serveur
  const loadWishlist = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await wishlistApi.get('/api/wishlist');
      const wishlistItems = response.data.items || [];
      
      dispatch({ type: 'SET_WISHLIST', payload: wishlistItems });
    } catch (error) {
      console.error('Erreur lors du chargement de la wishlist:', error);
      dispatch({ 
        type: 'SET_ERROR', 
        payload: 'Impossible de charger votre liste de souhaits' 
      });
    }
  }, [isAuthenticated]);

  // Ajouter un produit à la wishlist
  const addToWishlist = useCallback(async (productId) => {
    if (!isAuthenticated) {
      return false;
    }

    try {
      const response = await wishlistApi.post('/api/wishlist/add', { product_id: productId });
      
      // Recharger la wishlist pour avoir les données complètes
      await loadWishlist();
      
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'ajout à la wishlist:', error);
      return false;
    }
  }, [isAuthenticated, loadWishlist]);

  // Retirer un produit de la wishlist
  const removeFromWishlist = useCallback(async (itemId) => {
    if (!isAuthenticated) {
      return false;
    }

    try {
      await wishlistApi.delete(`/api/wishlist/remove/${itemId}`);
      
      dispatch({ type: 'REMOVE_ITEM', payload: itemId });
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      return false;
    }
  }, [isAuthenticated]);

  // Vider complètement la wishlist
  const clearWishlist = useCallback(async () => {
    if (!isAuthenticated) {
      return false;
    }

    try {
      await wishlistApi.delete('/api/wishlist/clear');
      
      dispatch({ type: 'CLEAR_WISHLIST' });
      return true;
    } catch (error) {
      console.error('Erreur lors du vidage:', error);
      return false;
    }
  }, [isAuthenticated]);

  // Vérifier si un produit est dans la wishlist
  const isInWishlist = useCallback((productId) => {
    return state.items.some(item => item.product_id === productId);
  }, [state.items]);

  // Obtenir l'ID de l'élément wishlist pour un produit
  const getWishlistItemId = useCallback((productId) => {
    const item = state.items.find(item => item.product_id === productId);
    return item ? item.id : null;
  }, [state.items]);

  // Basculer l'état wishlist d'un produit (ajouter/retirer)
  const toggleWishlist = useCallback(async (productId) => {
    if (isInWishlist(productId)) {
      const itemId = getWishlistItemId(productId);
      if (itemId) {
        return await removeFromWishlist(itemId);
      }
    } else {
      return await addToWishlist(productId);
    }
    return false;
  }, [isInWishlist, getWishlistItemId, addToWishlist, removeFromWishlist]);

  // Charger la wishlist quand l'utilisateur se connecte
  useEffect(() => {
    if (isAuthenticated && !state.initialized) {
      loadWishlist();
    } else if (!isAuthenticated) {
      dispatch({ type: 'CLEAR_WISHLIST' });
    }
  }, [isAuthenticated, state.initialized, loadWishlist]);

  // Recharger la wishlist quand l'utilisateur change
  useEffect(() => {
    if (isAuthenticated && user) {
      loadWishlist();
    }
  }, [user?.id, isAuthenticated, loadWishlist]);

  const value = {
    // État
    items: state.items,
    loading: state.loading,
    error: state.error,
    initialized: state.initialized,
    
    // Actions
    addToWishlist,
    removeFromWishlist,
    clearWishlist,
    toggleWishlist,
    loadWishlist,
    
    // Utilitaires
    isInWishlist,
    getWishlistItemId,
    
    // Statistiques
    itemCount: state.items.length,
    isEmpty: state.items.length === 0
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
